import { Component, OnInit } from '@angular/core';
import { BaseComponent } from '../../../core/abstractions/base.component';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import {
    EMPLOYEES_COLLECTION,
    EMPLOYEES_READ_ONLY_COLLECTION,
    HELPER_EMPLOYEE_ID_TARGET_STORAGE_KEY,
    READY_FOR_CONSULTATION_PROCESS,
} from '../../../app.constants';
import { concatMap, map } from 'rxjs';
import { AuthFirebaseService } from '../../../core/services/auth-firebase.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UserService } from '../../../core/services/user.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../../../core/components/confirm-dialog/confirm-dialog.component';
import { StorageService } from '../../../core/services/storage.service';
import { CopHomeService } from '../cop-home/cop-home.service';
import { ActivatedRoute } from '@angular/router';
import { FinsteinHelperTypesEnum } from '../../../shared/enums/finstein-helper-types.enum';

@UntilDestroy()
@Component({
    selector: 'app-cop-helper',
    templateUrl: './cop-helper.component.html',
    styleUrls: ['./cop-helper.component.scss'],
})
export class CopHelperComponent extends BaseComponent implements OnInit {
    employees: any[];
    loadingEmployees = false;

    constructor(
        private firestore: AngularFirestore,
        private auth: AuthFirebaseService,
        private userService: UserService,
        private dialog: MatDialog,
        private storage: StorageService,
        private copHomeService: CopHomeService,
        private activatedRoute: ActivatedRoute,
    ) {
        super();
    }

    override ngOnInit(): void {
        this.loadingEmployees = true;
        this.spinner.show();

        this.activatedRoute.params
            .pipe(
                untilDestroyed(this),
                concatMap((params: any) => {
                    if (params.id) {
                        return this.firestore
                            .collection(EMPLOYEES_READ_ONLY_COLLECTION, (ref) =>
                                ref.where('status', 'in', [
                                    'READY_FOR_CONSULTATION_PROCESS',
                                    'IN_THE_CONSULTING_PROCESS',
                                ]).where('companyId', '==', params.id)
                            )
                            .get()
                    } else {
                        return this.firestore
                            .collection(EMPLOYEES_READ_ONLY_COLLECTION, (ref) =>
                                ref.where('status', 'in', [
                                    'READY_FOR_CONSULTATION_PROCESS',
                                    'IN_THE_CONSULTING_PROCESS',
                                ])
                            )
                            .get()
                    }
                }),
                map((actions) =>
                    actions.docs.map((doc) => {
                        const data: any = doc.data();
                        data.id = doc.id;
                        return data;
                    })
                )
            )
            .subscribe({
                next: (resp: any) => {
                    this.spinner.hide();
                    this.loadingEmployees = false;
                    this.employees = resp;
                },
                error: (err) => {
                    this.spinner.hide();
                    this.loadingEmployees = false;
                    console.error('Error:', err);
                },
            });
    }

    onSelectEployee(row: any) {
        this.dialog
            .open(ConfirmDialogComponent, {
                panelClass: 'curved-modal',
                hasBackdrop: true,
                data: {
                    title: 'cop.helper.confirm-select-employee-modal.title',
                    text: 'cop.helper.confirm-select-employee-modal.text',
                },
            })
            .afterClosed()
            .subscribe((result) => {
                if (result) {
                    this.spinner.show();
                    this.storage.save(
                        HELPER_EMPLOYEE_ID_TARGET_STORAGE_KEY,
                        row.id
                    );
                    this.userService
                        .listenForChangesAtUser(EMPLOYEES_COLLECTION, row.id)
                        .subscribe({
                            next: () => {
                                if (
                                    this.userService.loggedUser.status ===
                                    READY_FOR_CONSULTATION_PROCESS
                                ) {
                                    this.copHomeService
                                        .updateEmployeeStatusToConsultancyProcessStarted()
                                        .pipe(map(() => this.userService.loggedUser));
                                }
                                this.navigate(['/consultancy-process']);
                                this.spinner.hide();
                            },
                            error: (err) => {
                                console.log(err);
                            },
                        });
                }
            });
    }

    onLogout() {
        this.spinner.show();
        this.auth
            .signOut()
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => {
                    this.spinner.hide();
                    this.navigate(['/auth']);
                },
                error: () => {
                    this.spinner.hide();
                }
            });
    }

    get showBackButton(): boolean {
        return this.userService.loggedUser?.finsteinHelperType === FinsteinHelperTypesEnum.BROKER_HELPER;
    }

    backToList() {
        this.navigate(['/helper/broker']);
    }
}
